import React from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import { ORDER_COUNT_KEYS } from '../../../config/constants/analytics';
import {
  getIntervalAndMax,
  getNonZeroStackedAxisSeries,
} from '../../../utils/nativeAnalyticsUtils';
import { readablePrice } from '../../../utils/getCurrency';
import { getBaseChartOptions, mergeChartOptions } from '../theme/apexTheme';
import { getChannelColor } from '../theme/tokens';

const StackedBarChart = ({
  id,
  xAxisData,
  yAxisData,
  currencyObj,
  exportedCsvHeaderTitle,
}) => {
  const isOrderCountChart = Object.values(ORDER_COUNT_KEYS).includes(id);
  const flattenedYAxisData =
    yAxisData && yAxisData.length
      ? yAxisData
          .reduce((acc, curr) => {
            curr.data.forEach((value, index) => {
              acc[index] = (acc[index] || 0) + parseFloat(value);
            });
            return acc;
          }, [])
          .map(value => Math.round(value * 100) / 100)
      : [];

  const maxValueOfSeries = Math.max(...flattenedYAxisData);
  const { yAxisMaxValue, yAxisTickAmount } =
    getIntervalAndMax(maxValueOfSeries) || {};

  const { xAxisSeries, yAxisSeries } = getNonZeroStackedAxisSeries(
    xAxisData,
    yAxisData,
  );

  // Only show channels that actually have data — otherwise the legend lists
  // every provider (incl. ones with zero volume), which reads as clutter.
  const activeSeries = (yAxisSeries || []).filter(
    s => Array.isArray(s.data) && s.data.some(v => Math.abs(+v) > 0),
  );

  // Canonical channel colours: use a series' own colour if present, else map by
  // provider id, else fall back to the categorical palette (brand/branch case).
  const seriesColors = activeSeries.map(
    (s, i) => s.color || getChannelColor(s.provider_id, i),
  );

  const chartConfig = {
    series: activeSeries,
    options: mergeChartOptions(getBaseChartOptions(), {
      colors: seriesColors,
      stroke: { width: 0 },
      tooltip: {
        y: {
          formatter(value) {
            return isOrderCountChart
              ? value
              : readablePrice(value * 100, currencyObj);
          },
        },
      },
      chart: {
        id,
        type: 'bar',
        stacked: true,
        toolbar: {
          show: true,
          export: {
            csv: {
              filename: `provider_wise_${id}`,
              headerCategory: exportedCsvHeaderTitle,
              headerValue: 'value',
            },
            svg: { filename: `provider_wise_${id}` },
            png: { filename: `provider_wise_${id}` },
          },
        },
      },
      xaxis: {
        type: 'category',
        categories: xAxisSeries,
        labels: { hideOverlappingLabels: false, trim: true },
      },
      yaxis: {
        max: yAxisMaxValue || 10,
        tickAmount: yAxisTickAmount || 1,
        labels: {
          formatter(val) {
            return val.toFixed(0);
          },
        },
      },
    }),
  };
  return (
    <Chart
      options={chartConfig.options}
      series={chartConfig.series}
      type="bar"
      height="350"
    />
  );
};

StackedBarChart.propTypes = {
  xAxisData: PropTypes.array,
  yAxisData: PropTypes.array,
  id: PropTypes.string,
  currencyObj: PropTypes.object,
  exportedCsvHeaderTitle: PropTypes.string,
};
export default StackedBarChart;
